import { HttpErrorResponse } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { DeliveryTrackerDocumentErrorCodeMap } from '@core/config/locally-handled-domain-errors.config';
import { DocumentDownloadService } from '@core/services/document-download.service';
import { DeliveryTrackerNotifyOnlyStatuses } from '@features/organization/components/delivery-tracker-detail/configuration/delivery-tracker-result-upload.config';
import { DeliveryTrackerDetailFacade } from '@features/organization/components/delivery-tracker-detail/facades/delivery-tracker-detail.facade';
import { DeliveryTrackerDocumentService } from '@features/organization/components/delivery-tracker-detail/services/delivery-tracker-document.service';
import { DeliveryTrackerDocumentStore } from '@features/organization/components/delivery-tracker-detail/stores/delivery-tracker-document.store';
import type {
  DeliveryTrackerDocument,
  DeliveryTrackerResultDelivery,
} from '@features/organization/components/delivery-tracker-detail/types/delivery-tracker-document.types';
import type { DeliveryTrackerDocumentDeleteState } from '@features/organization/components/delivery-tracker-detail/utility/delivery-tracker-document-delete.utility';
import { getDeliveryTrackerDocumentDeleteState } from '@features/organization/components/delivery-tracker-detail/utility/delivery-tracker-document-delete.utility';
import { HttpStatusMap } from '@kerberos-compliance/compliance-shared-ts/common/enums/http-status.enum';
import { SortOrderMap } from '@kerberos-compliance/compliance-shared-ts/common/enums/sort-order.enum';
import {
  PaginationMap,
  PaginationPageSizeMap,
} from '@kerberos-compliance/compliance-shared-ts/common/settings/pagination.settings';
import type { UUID } from '@kerberos-compliance/compliance-shared-ts/common/types/uuid.type';
import type { ResponseDeliveryTrackerCacheDto } from '@kerberos-compliance/compliance-shared-ts/features/delivery/dtos/response.delivery-tracker.cache-dto';
import {
  AuditJustificationService,
  createAsyncErrorHandler,
  DocumentPreviewModalService,
  ErrorHandlerService,
  ErrorSeverityMap,
  NotificationService,
  TranslationService,
} from '@kerberos-compliance/compliance-shared-ui';
import { getDomainError } from '@shared/utils/domain-error.utility';
import { createFacadeFailureOptions } from '@shared/utils/facade-failure.utility';

const NotificationKeys = {
  loadFailed: 'organizations.detailPage.riskAnalysisDeliveryTab.documents.notifications.loadFailed',
  downloadFailed: 'notifications.download.failed',
  deliverSucceeded: 'organizations.detailPage.riskAnalysisDeliveryTab.documents.notifications.deliverSucceeded',
  deliverAndCloseSucceeded:
    'organizations.detailPage.riskAnalysisDeliveryTab.documents.notifications.deliverAndCloseSucceeded',
  deliverFailed: 'organizations.detailPage.riskAnalysisDeliveryTab.documents.notifications.deliverFailed',
  deliverAndNotifySucceeded:
    'organizations.detailPage.riskAnalysisDeliveryTab.documents.notifications.deliverAndNotifySucceeded',
  deleteSucceeded: 'organizations.detailPage.riskAnalysisDeliveryTab.documents.notifications.deleteSucceeded',
  deleteFailed: 'organizations.detailPage.riskAnalysisDeliveryTab.documents.notifications.deleteFailed',
  deleteLastRiskAnalysis:
    'organizations.detailPage.riskAnalysisDeliveryTab.documents.notifications.deleteLastRiskAnalysis',
  documentNotFound: 'organizations.detailPage.riskAnalysisDeliveryTab.documents.notifications.documentNotFound',
} as const;

const DeleteDialogKeys = {
  title: 'organizations.detailPage.riskAnalysisDeliveryTab.documents.deleteDialog.title',
  text: 'organizations.detailPage.riskAnalysisDeliveryTab.documents.deleteDialog.text',
  confirm: 'organizations.detailPage.riskAnalysisDeliveryTab.documents.deleteDialog.confirm',
} as const;

const DeleteErrorNotificationKeys: Readonly<Record<number, string>> = {
  [DeliveryTrackerDocumentErrorCodeMap.LastRiskAnalysis]: NotificationKeys.deleteLastRiskAnalysis,
  [DeliveryTrackerDocumentErrorCodeMap.ProcessDocumentNotFound]: NotificationKeys.documentNotFound,
};

const DocumentsSortField = 'createdAt';

@Injectable()
export class DeliveryTrackerDocumentFacade {
  private readonly deliveryTrackerDocumentStore = inject(DeliveryTrackerDocumentStore);

  public readonly deliveringTrackerId = this.deliveryTrackerDocumentStore.deliveringTrackerId;

  public readonly deletingDocumentId = this.deliveryTrackerDocumentStore.deletingDocumentId;

  private readonly documentService = inject(DeliveryTrackerDocumentService);

  private readonly documentDownloadService = inject(DocumentDownloadService);

  private readonly documentPreviewModalService = inject(DocumentPreviewModalService);

  private readonly detailFacade = inject(DeliveryTrackerDetailFacade);

  private readonly notificationService = inject(NotificationService);

  private readonly translationService = inject(TranslationService);

  private readonly auditJustificationService = inject(AuditJustificationService);

  private readonly asyncErrorHandler = createAsyncErrorHandler();

  private readonly errorHandlerService = inject(ErrorHandlerService);

  private readonly failure = createFacadeFailureOptions('DeliveryTrackerDocumentFacade');

  /**
   * Returns the documents cached for one tracker.
   *
   * @param {UUID} trackerId - The tracker whose table is rendering.
   * @returns {DeliveryTrackerDocument[]} The tracker's documents.
   */
  public documentsForTracker(trackerId: UUID): DeliveryTrackerDocument[] {
    return this.deliveryTrackerDocumentStore.getDocumentsForTracker(trackerId);
  }

  /**
   * Whether one tracker's documents are being fetched.
   *
   * @param {UUID} trackerId - The tracker to check.
   * @returns {boolean} True while the fetch is in flight.
   */
  public isLoadingForTracker(trackerId: UUID): boolean {
    return this.deliveryTrackerDocumentStore.isLoadingForTracker(trackerId);
  }

  /**
   * Decides how one row of a tracker's document table offers its delete action, from the documents currently
   * listed for that tracker.
   *
   * @param {ResponseDeliveryTrackerCacheDto} tracker - The tracker the table belongs to.
   * @param {DeliveryTrackerDocument} document - The row to decide for.
   * @returns {DeliveryTrackerDocumentDeleteState} How the row's delete action is offered.
   */
  public deleteStateFor(
    tracker: ResponseDeliveryTrackerCacheDto,
    document: DeliveryTrackerDocument,
  ): DeliveryTrackerDocumentDeleteState {
    return getDeliveryTrackerDocumentDeleteState(tracker.status, this.documentsForTracker(tracker.id), document);
  }

  /**
   * Fetches one tracker's documents unless they are already cached. A failed fetch is reported and leaves the
   * table empty rather than blocking the rest of the panel. The panel's table is unpaginated, so the fetch asks
   * for the largest page the API supports - a page-sized fetch would silently hide the rest of the documents.
   *
   * @param {UUID} trackerId - The tracker whose documents to load.
   * @param {boolean} [shouldRefetch] - Refetch even when the documents are cached, e.g. right after a delivery.
   * @returns {Promise<void>} Resolves once the fetch has settled.
   */
  public async loadDocuments(trackerId: UUID, shouldRefetch: boolean = false): Promise<void> {
    if (!shouldRefetch && this.deliveryTrackerDocumentStore.hasDocumentsForTracker(trackerId)) {
      return;
    }

    this.deliveryTrackerDocumentStore.setLoadingForTracker(trackerId, true);
    try {
      const response = await this.asyncErrorHandler(
        async () =>
          this.documentService.searchDeliveryTrackerDocuments(trackerId, {
            page: PaginationMap.PageDefault,
            pageSize: PaginationPageSizeMap.Maximum,
            sortField: DocumentsSortField,
            sortOrder: SortOrderMap.Descending,
            countAll: true,
          }),
        this.failure('loadDocuments', NotificationKeys.loadFailed),
      );
      this.deliveryTrackerDocumentStore.setDocumentsForTracker(trackerId, response?.data ?? []);
    } finally {
      this.deliveryTrackerDocumentStore.setLoadingForTracker(trackerId, false);
    }
  }

  /**
   * Opens the inline preview for one document. The modal fetches the bytes itself and falls back to the
   * regular download while they are not loaded yet.
   *
   * @param {DeliveryTrackerDocument} document - The row whose file name was clicked.
   * @returns {void} This method does not return a value.
   */
  public previewDocument(document: DeliveryTrackerDocument): void {
    this.documentPreviewModalService.open({
      fileName: document.displayName,
      loadFile: () => this.documentDownloadService.downloadDocument(document.id),
      onDownload: () => this.downloadDocument(document),
    });
  }

  /**
   * Hands one document's bytes to the browser as a download.
   *
   * @param {DeliveryTrackerDocument} document - The document row that was clicked.
   * @returns {Promise<void>} Resolves once the download was handed over.
   */
  public async downloadDocument(document: DeliveryTrackerDocument): Promise<void> {
    try {
      await this.documentDownloadService.downloadAndSaveDocument(document.id, document.displayName);
    } catch (error: unknown) {
      await this.handleDownloadFailure(document, error);
    }
  }

  /**
   * Asks for the mandatory justification, then soft-deletes one result document of a tracker and reloads the
   * table. Dismissing the dialog deletes nothing. A rejection the panel can explain (the last risk analysis, a
   * document already gone) gets its own message and reloads the list, since the list the agent acted on was stale.
   *
   * @param {ResponseDeliveryTrackerCacheDto} tracker - The tracker the document belongs to.
   * @param {DeliveryTrackerDocument} document - The result document to delete.
   * @returns {Promise<boolean>} True when the API deleted the document.
   */
  public async deleteDocument(
    tracker: ResponseDeliveryTrackerCacheDto,
    document: DeliveryTrackerDocument,
  ): Promise<boolean> {
    const justification = await this.auditJustificationService.request({
      required: true,
      title: this.translationService.getTranslation(DeleteDialogKeys.title),
      description: this.translationService.getTranslation(DeleteDialogKeys.text, { displayName: document.displayName }),
      confirmLabel: this.translationService.getTranslation(DeleteDialogKeys.confirm),
    });
    if (!justification) {
      return false;
    }

    this.deliveryTrackerDocumentStore.setDeletingDocumentId(document.id);
    let shouldReload = false;
    try {
      const succeeded = await this.asyncErrorHandler(
        async () => {
          await this.documentService.deleteDocument(tracker.id, document.id, justification);
          return true;
        },
        {
          ...this.failure('deleteDocument', NotificationKeys.deleteFailed, ErrorSeverityMap.High),
          // The rejections the panel explains are handled locally, so the error handler shows no toast for them
          onError: (error: unknown) => {
            const notificationKey = DeleteErrorNotificationKeys[getDomainError(error) ?? 0];
            if (notificationKey === undefined) {
              return;
            }
            shouldReload = true;
            this.notificationService.error(this.translationService.getTranslation(notificationKey));
          },
        },
      );

      if (succeeded) {
        this.notificationService.success(this.translationService.getTranslation(NotificationKeys.deleteSucceeded));
      }
      if (succeeded || shouldReload) {
        await this.loadDocuments(tracker.id, true);
      }
      return succeeded ?? false;
    } finally {
      this.deliveryTrackerDocumentStore.setDeletingDocumentId(undefined);
    }
  }

  /**
   * Submits a result delivery for one tracker and refreshes what it changed: the tracker's document table
   * always, and the tracker list as well when the delivery closed the tracker, so the stepper and the status
   * badge stop showing the pre-delivery state.
   *
   * @param {ResponseDeliveryTrackerCacheDto} tracker - The tracker receiving the documents.
   * @param {DeliveryTrackerResultDelivery} delivery - The staged files and the close-and-notify decision.
   * @returns {Promise<boolean>} True when the API accepted the delivery.
   */
  public async deliverResult(
    tracker: ResponseDeliveryTrackerCacheDto,
    delivery: DeliveryTrackerResultDelivery,
  ): Promise<boolean> {
    this.deliveryTrackerDocumentStore.setDeliveringTrackerId(tracker.id);
    try {
      const succeeded = await this.asyncErrorHandler(
        async () => {
          await this.documentService.deliverResult(tracker.id, delivery);
          return true;
        },
        this.failure('deliverResult', NotificationKeys.deliverFailed, ErrorSeverityMap.High),
      );

      if (!succeeded) {
        return false;
      }

      const isNotifyOnly = DeliveryTrackerNotifyOnlyStatuses.includes(tracker.status);
      const hasClosedTracker = delivery.closeTrackerAndNotify && !isNotifyOnly;
      this.notificationService.success(
        this.translationService.getTranslation(this.deliverySuccessKey(delivery.closeTrackerAndNotify, isNotifyOnly)),
      );
      // The delivery is saved at this point, so a failed refresh must not report it as failed: the dialog would stay
      // open and a retry would deliver the same files - and notify the customer
      try {
        await this.loadDocuments(tracker.id, true);
        if (hasClosedTracker) {
          await this.detailFacade.loadDeliveryTrackerByOrganizationId(tracker.organizationId);
        }
      } catch {
        // Already reported by the failing refresh.
      }
      return true;
    } finally {
      this.deliveryTrackerDocumentStore.setDeliveringTrackerId(undefined);
    }
  }

  /**
   * Picks the success message of a delivery: a plain delivery, one that also closed the tracker, or - on a
   * tracker that was already complete - one that only notified the customer again.
   *
   * @param {boolean} shouldCloseAndNotify - The decision the delivery carried.
   * @param {boolean} isNotifyOnly - Whether the tracker was already complete, so the decision only notified.
   * @returns {string} The translation key of the message.
   */
  private deliverySuccessKey(shouldCloseAndNotify: boolean, isNotifyOnly: boolean): string {
    if (!shouldCloseAndNotify) {
      return NotificationKeys.deliverSucceeded;
    }
    return isNotifyOnly ? NotificationKeys.deliverAndNotifySucceeded : NotificationKeys.deliverAndCloseSucceeded;
  }

  /**
   * Reports a failed download with one toast. A document that is gone (deleted from another tab or by another agent)
   * gets its own message and the list is reloaded, rather than the generic failure.
   *
   * @param {DeliveryTrackerDocument} document - The document whose download failed.
   * @param {unknown} error - The error the download failed with.
   * @returns {Promise<void>} Resolves once the failure is reported and any reload settled.
   */
  private async handleDownloadFailure(document: DeliveryTrackerDocument, error: unknown): Promise<void> {
    const isGone = error instanceof HttpErrorResponse && error.status === HttpStatusMap.NotFound;
    this.errorHandlerService.handleError({
      error,
      ...this.failure('downloadDocument', isGone ? NotificationKeys.documentNotFound : NotificationKeys.downloadFailed),
    });
    if (isGone && document.deliveryTrackerId) {
      await this.loadDocuments(document.deliveryTrackerId, true);
    }
  }
}
